import { useState, useEffect, createContext, useRef } from "react";
import { getMainView, getViewerView, getSideViewerView, callApi, getBottomViewerView, getCookie, setCookie } from "./Helpers";
import { useParams } from "react-router-dom";
import Viewer from "./components/Viewer";
import MainBody from "./components/MainBody";
import Backdrop from "./components/Backdrop";
import SideViewer from "./components/SideViewer";
import ConfirmDialog from "./components/ConfirmDialog";
import { ToastContainer, toast } from 'react-toastify';
import SideBar from "./components/SideBar";
import TopBar from "./components/TopBar";
import MainMenuBtn from "./components/MainMenuBtn";
import Launcher from "./views/Launcher";
import OverlayLoader from "./components/OverlayLoader";
import MainLoader from "./components/MainLoader";
import BottomBar from "./components/BottomBar";
import BottomViewer from "./components/BottomViewer";
import { STR_COULD_NOT_FETCH_DATA } from "./Strings";
import PinChecker from "./components/PinChecker";
import MobileInterface from "./MobileInterface";


export const AppContext = createContext(null);

export default function App(props) {

  const [ready, setReady] = useState(false); //for checking if app is ready

  const { _navItem, _navSubItem, _navExtraItem, _navMoreItem } = useParams(); //for routing purposes

  const [navItem, setNavItem] = useState(_navItem); //routing 
  const [navSubItem, setNavSubItem] = useState(_navSubItem); //routing
  const [navExtraItem, setNavExtraItem] = useState(_navExtraItem); //routing
  const [navMoreItem, setNavMoreItem] = useState(_navMoreItem); //routing

  const [showViewer, setShowViewer] = useState(false); //controlling the display of Viewer component
  const [viewerView, setViewerView] = useState(null); //the view to be shown in viewer
  const [viewerTopView, setViewerTopView] = useState(null);

  const [showSideViewer, setShowSideViewer] = useState(false); //controlling the display of SideViewer component
  const [sideViewerView, setSideViewerView] = useState(null); //the view to be shown in viewer

  const [showBottomViewer, setShowBottomViewer] = useState(false); //controlling the display of BottomViewer component
  const [bottomViewerView, setBottomViewerView] = useState(null); //the view to be shown in viewer

  const [mainView, setMainView] = useState(null); //the view tobe shown in MainBody

  const [showOverlayLoader, setShowOverlayLoader] = useState(false); //controlling the display of OverlayLoader

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [confirmDialogMessage, setConfirmDialogMessage] = useState("");
  const [confirmDialogAction, setConfirmDialogAction] = useState("");

  const [isSideBarOpen, setIsSideBarOpen] = useState(false); //controls open and close for sidebar in mobile devices

  const [auth, setAuth] = useState(false); //track user authorization status

  //currentSession
  const [currentUserData, setCurrentUserData] = useState(null);
  const [theme, setTheme] = useState('default');
  const [language, setLanguage] = useState(getCookie('language') || 'sw');

  //App level data
  const [systemParams, setSystemParams] = useState(null);
  const [myOrders, setMyOrders] = useState(null);
  const [mySales, setMySales] = useState(null);
  const [myItems, setMyItems] = useState(null);
  const [myRevenues, setMyRevenues] = useState(null);
  const [myWithdrawals, setMyWithdrawals] = useState(null);
  const [myWithdrawalRequests, setMyWithdrawalRequests] = useState(null);
  const [myAccountBalance, setMyAccountBalance] = useState(null);
  const [itemsFeed, setItemsFeed] = useState(null);
  
  

  async function init() {
    /**
     * Initialize the app here
     */

    setReady(false);
    await getSystemParams().then(async () => {
      await authCheck().then(async (_auth) => {
        if (_auth) {
          await getCurrentUserData()
        }
      })

      //turn off loaders after initialization
      setReady(true);
    })


  }

  function changeLanguage() {
    if (language === 'en') {
      setLanguage('sw');
    } else {
      setLanguage('en');
    }
  }


  function navBack() {
    window.history.back();
    setShowOverlayLoader(false);
  }

  function tellError(msg) {
    toast.error(msg);
  }

  function tellInfo(msg) {
    toast.info(msg);
  }

  function tellWarning(msg) {
    toast.warn(msg);
  }

  function tellMessage(msg) {
    toast.success(msg);
  }

  function refresh() {
    /**
     * This function refreshes the whole app
     */
    window.location.reload(); //remember to optimize
  }

  function navTo(nav) {
    /**
     * This function handles navigation inside the app
     * Utilizing Hash based routing
     * nav is the object supporting the following keys: item, subItem, extraItem, moreItem
     */
    if (nav) {
      //..

      let url = '';
      if (nav.item) {
        url = `#/${nav.item}/`
      }

      if (nav.subItem) {
        url += `${nav.subItem}/`
      }

      if (nav.extraItem) {
        url += `${nav.extraItem}/`
      }

      if (nav.moreItem) {
        url += `${nav.moreItem}/`
      }

      window.location.href = url;
      //..
    }
  }

  function getCurrentUserData() {
    return new Promise(async (resolve, reject) => {
      await callApi('get-current-user-data', {}).then(response => {
        if (response.status === 1) {
          setCurrentUserData(response.data);
          resolve(response.data);
        }
      })
    })
  }

  
  async function getSystemParams() {

    return new Promise(async resolve => {
      await callApi('get-system-params', {}).then(response => {
        if (response.status === 1) {
          setSystemParams(response.data);
          resolve(response.data)
        } else {
          resolve(null)
        }
      })
    })

  }



  function authCheck() {
    return new Promise(async resolve => {
      await callApi('auth-check', {}).then(response => {
        if (response.status === 1) {
          setAuth(true);
          resolve(true)
        } else {
          setAuth(false);
          resolve(false);
        }
      })
    })
  }

  
  function activateDialog(params) {
    let {
      message,
      onConfirm
    } = params;
    setConfirmDialogAction(() => { return onConfirm });
    setConfirmDialogMessage(message)
    setShowConfirmDialog(true);
  }

  function getMyOrders() {
    return new Promise(async (resolve) => {
      await callApi("get-my-orders", {}).then(response => {
        if (response.status === 1) {
          setMyOrders(response.data);
          resolve(response.data);
        } else {
          tellError(STR_COULD_NOT_FETCH_DATA[language]);
          resolve(null);
        }
      })
    })
  }

  function getMyWithdrawals() {
    return new Promise(async (resolve) => {
      await callApi("get-my-withdrawals", {}).then(response => {
        if (response.status === 1) {
          setMyWithdrawals(response.data);
          resolve(response.data);
        } else {
          tellError(STR_COULD_NOT_FETCH_DATA[language]);
          resolve(null);
        }
      })
    })
  }

  function getMyWithdrawalRequests() {
    return new Promise(async (resolve) => {
      await callApi("get-my-withdrawal-requests", {}).then(response => {
        if (response.status === 1) {
          setMyWithdrawalRequests(response.data);
          resolve(response.data);
        } else {
          tellError(STR_COULD_NOT_FETCH_DATA[language]);
          resolve(null);
        }
      })
    })
  }

  function getMyRevenues() {
    return new Promise(async (resolve) => {
      await callApi("get-my-revenues", {}).then(response => {
        if (response.status === 1) {
          setMyRevenues(response.data);
          resolve(response.data);
        } else {
          tellError(STR_COULD_NOT_FETCH_DATA[language]);
          resolve(null);
        }
      })
    })
  }

  function getMyAccountBalance() {
    return new Promise(async (resolve) => {
      await callApi("get-my-account-balance", {}).then(response => {
        if (response.status === 1) {
          setMyAccountBalance(response.data);
          resolve(response.data);
        } else {
          tellError(STR_COULD_NOT_FETCH_DATA[language]);
          resolve(null);
        }
      })
    })
  }

  function getMySales() {
    return new Promise(async (resolve) => {
      await callApi("get-my-sales", {}).then(response => {
        if (response.status === 1) {
          setMySales(response.data);
          resolve(response.data);
        } else {
          tellError(STR_COULD_NOT_FETCH_DATA[language]);
          resolve(null);
        }
      })
    })
  }

  function getMyItems() {
    return new Promise(async (resolve) => {
      await callApi("get-my-items", {}).then(response => {
        if (response.status === 1) {
          setMyItems(response.data);
          resolve(response.data);
        } else {
          tellError(STR_COULD_NOT_FETCH_DATA[language]);
          resolve(null);
        }
      })
    })
  }

  function getItemsFeed() {
    return new Promise(async (resolve) => {
      await callApi("get-items-feed", {}).then(response => {
        if (response.status === 1) {
          setItemsFeed(response.data);
          resolve(response.data);
        } else {
          tellError(STR_COULD_NOT_FETCH_DATA[language]);
          resolve(null);
        }
      })
    })
  }

  function getRegions() {
    return new Promise(async (resolve) => {
      await callApi("napa-get-regions", {}).then(response => {
        if (response.status === 1) {
          resolve(response.data);
        } else {
          tellError(STR_COULD_NOT_FETCH_DATA[language]);
          resolve(null);
        }
      })
    })
  }

  function getDistricts(params) {
    return new Promise(async (resolve) => {
      let {
        regionId,
      } = params;

      await callApi("napa-get-districts", { regionId }).then(response => {
        if (response.status === 1) {
          resolve(response.data);
        } else {
          tellError(STR_COULD_NOT_FETCH_DATA[language]);
          resolve(null);
        }
      })
    })
  }

  function getWards(params) {
    return new Promise(async (resolve) => {
      let {
        districtId,
      } = params;

      await callApi("napa-get-wards", { districtId }).then(response => {
        if (response.status === 1) {
          resolve(response.data);
        } else {
          tellError(STR_COULD_NOT_FETCH_DATA[language]);
          resolve(null);
        }
      })
    })
  }

  function getStreets(params) {
    return new Promise(async (resolve) => {
      let {
        wardId,
      } = params;

      await callApi("napa-get-streets", { wardId }).then(response => {
        if (response.status === 1) {
          resolve(response.data);
          
        } else {
          resolve(null);
          tellError(STR_COULD_NOT_FETCH_DATA[language]);
        }
      })
    })
  }

  
  
  useEffect(() => {
    init();

    
  }, [])

  useEffect(() => {
    if (isSideBarOpen) {
      document.body.classList.add('sidebar-open');
    } else {
      document.body.classList.remove('sidebar-open');
    }
  }, [isSideBarOpen])

  useEffect(() => {
    setNavItem(_navItem);
    setNavSubItem(_navSubItem);
    setNavExtraItem(_navExtraItem);
    setNavMoreItem(_navMoreItem);
  }, [_navItem, _navSubItem, _navExtraItem, _navMoreItem])

  useEffect(() => {
    if (currentUserData) {
      MobileInterface.setOneSignalExternalUserId('sokobora-real-user-'+currentUserData.id);
    }
  }, [currentUserData])


  useEffect(() => {

    //check for viewers
    if (navItem === 'view') {
      //activate viewer
      setShowViewer(true);
      setViewerView(getViewerView(appContext));

      //hide other viewers
      setShowSideViewer(false)
      setSideViewerView(null);

      setShowBottomViewer(false);
      setBottomViewerView(null);

    } else if (navItem === 'side-view') {
      //activate viewer
      setShowSideViewer(true);
      setSideViewerView(getSideViewerView(appContext));

      //hide other viewers
      //setShowViewer(false)
      //setViewerView(null);

      setShowBottomViewer(false);
      setBottomViewerView(null);

    } else if (navItem === 'bottom-view') {
      //activate viewer
      setShowBottomViewer(true);
      setBottomViewerView(getBottomViewerView(appContext));

      //do not hide other views

    } else {
      //just set normal views
      setShowViewer(false);
      setShowSideViewer(false)

      setViewerView(null);
      setSideViewerView(null);

      const _mainView = getMainView(appContext)
      if (_mainView) {
        setMainView(_mainView);
      }
    }
  }, [navItem, navSubItem, navExtraItem, navMoreItem]);

  useEffect(() => {
    setCookie('language', language);
  }, [language])

  const appContext = {
    refresh,
    navTo,
    mainView,
    viewerView,
    setShowOverlayLoader,
    navItem,
    navSubItem,
    navExtraItem,
    navMoreItem,
    setShowViewer,
    showViewer,
    navBack,
    showSideViewer,
    setShowSideViewer,
    sideViewerView,
    activateDialog,
    setShowConfirmDialog,
    confirmDialogAction,
    confirmDialogMessage,
    showConfirmDialog,
    tellError,
    tellInfo,
    tellMessage,
    tellWarning,
    isSideBarOpen,
    setIsSideBarOpen,
    showOverlayLoader,
    auth,
    currentUserData,
    theme,
    language,
    getCurrentUserData,
    bottomViewerView,
    showBottomViewer,
    changeLanguage,
    getSystemParams,
    systemParams,
    setLanguage,
    getRegions,
    getStreets,
    getWards,
    getDistricts,
    getMyOrders,
    myOrders,
    getMyItems,
    myItems,
    itemsFeed,
    getItemsFeed,
    mySales,
    getMySales,
    myRevenues,
    myAccountBalance,
    myWithdrawalRequests,
    myWithdrawals,
    getMyRevenues,
    getMyAccountBalance,
    getMyWithdrawalRequests,
    getMyWithdrawals,
  }

  if (ready) {
    if (auth) {
      if (currentUserData) {
        return (
          <AppContext.Provider value={appContext}>
            <MainBody />
            <TopBar />
            <BottomBar />
            <Viewer />
            <SideViewer />
            <ConfirmDialog />
            <OverlayLoader />
            <BottomViewer />
            
            <ToastContainer style={{ zIndex: "var(--maxIndex)" }} position="bottom-right" />
          </AppContext.Provider>
        )
        
      } else {
        return (
          <AppContext.Provider value={appContext}>
            <ToastContainer style={{ zIndex: "var(--maxIndex)" }} position="bottom-right" />
            <MainLoader />
          </AppContext.Provider>
        )
      }
    } else {
      return (
        <AppContext.Provider value={appContext}>
          <Viewer />
          <OverlayLoader />
          <ToastContainer style={{ zIndex: "var(--maxIndex)" }} position="bottom-right" />
          <Launcher />
        </AppContext.Provider>
      )
    }
  } else {
    return (
      <AppContext.Provider value={appContext}>
        <ToastContainer style={{ zIndex: "var(--maxIndex)" }} position="bottom-right" />
        <MainLoader />
      </AppContext.Provider>
    )
  }
}